import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../../../common/ConstantsLagoon';
import { ImageComponent } from '../../../../../../../common/ImageComponent';
import { SmartChoiceScoreBox } from '../../../../../../../common/SnackCard/SnackCard.styles';
import { useLagoon } from '../../../../../../../hooks/useLagoon';
import { ProviderType } from '../../../../../../../models/ProviderDetails';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../../../../../utils/providerNames.utils';
import {
  AvatarProviderCard,
  HorizontalLine,
  LayoutGroupStyled,
  ProviderNameWithScore,
  SmartChoiceScore,
  StyledDrawer,
} from './PickProviderModal.style';

type Props = {
  isPickProviderModalOpen: boolean;
  setIsPickProviderModalOpen: (a: boolean) => void;
  selectedProviders: any[];
  pickProviderIndex?: number;
  onSwapProviderPress?: (index: string) => void;
};

export const PickProviderModal = ({
  isPickProviderModalOpen,
  setIsPickProviderModalOpen,
  selectedProviders,
  pickProviderIndex,
  onSwapProviderPress = () => {},
}: Props) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = React.useState('0');

  const lagoonData = useLagoon('ui-messaging')();
  const swapProviderBtn = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_SWAP_PROVIDER_BUTTON,
  })?.message;
  const pickProviderModalTitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_PICK_A_PROVIDER,
  })?.message;

  useEffect(() => {
    const chatIcon = document.getElementById(
      'async-messaging-custom-chat-icon-holder'
    );
    if (isPickProviderModalOpen) {
      if (chatIcon) {
        chatIcon.style.display = 'none';
      }
    } else if (chatIcon) {
      chatIcon.style.display = '';
    }
    return () => {
      if (chatIcon) {
        chatIcon.style.display = '';
      }
    };
  }, [isPickProviderModalOpen]);

  if (pickProviderIndex === 0) {
    selectedProviders = selectedProviders.filter((_, index) => index !== 1);
  } else {
    selectedProviders = selectedProviders.filter((_, index) => index !== 0);
  }
  return (
    <StyledDrawer
      closeOnClickOutside={false}
      data-auto-testid="pick-provider-drawer"
      data-testid="pick-provider-drawer"
      footer={
        <Layout.Group>
          <Button
            data-auto-testid="compare-providers-swap"
            data-testid="compare-providers-swap"
            disabled={selectedIndex === '0'}
            onClick={() => {
              onSwapProviderPress(selectedIndex);
              setIsPickProviderModalOpen(false);
            }}
          >
            {swapProviderBtn}
          </Button>
        </Layout.Group>
      }
      isOpen={isPickProviderModalOpen}
      onClose={() => {
        setIsPickProviderModalOpen(false);
      }}
      position="bottom"
      size={'95%'}
      title={pickProviderModalTitle}
    >
      <RadioGroup
        data-testid="radio-btn-group"
        label=""
        onChange={(e) => setSelectedIndex(e.target.value)}
        value={selectedIndex}
      >
        {selectedProviders.map((provider, index) => {
          const nameAttrs = getNameAttrs(provider);
          return (
            <React.Fragment key={index}>
              <LayoutGroupStyled
                cssProps={{ bool: index === Number(selectedIndex), index }}
                data-testid={`provider-${index}`}
                key={index}
              >
                <RadioGroup.Radio label="" size={14} value={index.toString()} />
                <AvatarProviderCard>
                  {ImageComponent({
                    imgRef,
                    providerType: provider?.providerType,
                    imgSrc: provider?.imageLocation,
                    sizeToken: '$dataCardIconSize',
                  })}
                </AvatarProviderCard>
                <ProviderNameWithScore>
                  <Text color="#323333" fontWeight={600} size={16}>
                    {parseProviderName(
                      provider?.providerName,
                      ProviderType.PRACTITIONER,
                      provider?.primaryDegrees?.[0],
                      nameAttrs
                    )}
                  </Text>
                  <SmartChoiceScoreBox
                    css={{ borderRadius: '4px', margin: '3px 0px' }}
                  >
                    <SmartChoiceScore>{`${t('SMART_CHOICE.SMART_CHOICE')} ${
                      provider?.recommendationDetails
                        ?.totalRecommendationScore || 0
                    }%`}</SmartChoiceScore>
                  </SmartChoiceScoreBox>
                  <Text color="#323333" size={14}>
                    {provider?.address?.line[0]}, {provider?.address?.city},{' '}
                    {provider?.address?.state}
                  </Text>
                </ProviderNameWithScore>
              </LayoutGroupStyled>
              {index !== selectedProviders.length - 1 &&
                ![Number(selectedIndex), Number(selectedIndex) - 1].includes(
                  index
                ) && <HorizontalLine data-testid={'horizontal-line'} />}
            </React.Fragment>
          );
        })}
      </RadioGroup>
    </StyledDrawer>
  );
};
